import { enrichAiDevices } from "./devices/enrichAiDevices";
import { convertMcBimItemToDevice } from "./devices/deviceUtils";
import { selectCompCodePath } from "../globalSelectors";
import { AppDispatch, IAppThunk, IRootState } from "../app/store";
import { z } from "zod";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  addSelectedRoomToRedux,
  clearNonPublischedDevicesFromAlgosInRedux,
  getHeightFromFirstCeilingGrid,
  removeNonPublischedDevicesFromAlgosInRedux,
  removeSelectedRoomFromRedux,
  resetReviewModeInRedux,
  resetSpaceValidationRunInRedux,
  setActiveRoomColoringIdInRedux,
  setActiveLeftSideBarTabInRedux,
  setCopySpaceModeActiveInRedux,
  setCountAlgoSpaceComplete,
  setCountAlgoSpaceFailed,
  setCountAlgoSpaceTotal,
  setDragSelectSpacesInRedux,
  setMeasurementToolActiveInRedux,
  setSelectedRoomsInRedux,
  setSelectedRoomTabInRedux,
  setSelectedSpaceNumberInRedux,
  thunkToast,
} from "./designSlice";
import { selectDeviceById, selectHttpDevices } from "./devices/deviceSelectors";
import {
  clickOnDeviceThunk,
  copyDeviceThunk,
  rotateDeviceThunk,
  setSavingDevices,
} from "./devices/deviceSlice";
import { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { openapi } from "../../openapi";
import {
  RIGHT_CLICK_DEVICE_MENU_ENUM,
  DEVICE_DISCIPLINE_MAPPING,
  DisciplineEnum,
} from "@multiconsult-group/multiplacer-core";
import { selectActiveSingleRoomSummary } from "../Room/roomMcbimSharedSelectors";
import { generateRandomString, PartialMcBimSpace } from "../utils";
import {
  IDevice,
  IMLPredictDevice,
} from "@multiconsult-group/multiplacer-core";
import { ROOM_COLORING_IDS } from "../RoomColoring";
import { DesignLeftSideBarTabs, RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { copyDevicesFromASpaceToAnotherSpace } from "@multiconsult-group/multiplacer-core";
import {
  ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE,
} from "@multiconsult-group/multiplacer-core";
import { selectMcBimItemsForViewer } from "../McBim/mcbimItemSelectors";
import { selectFinalPixPolygons } from "./pixpolygonSelectors";
import {
  setClickedMcBimItemIdInRedux,
  setClickedObjectRoomInRedux,
  setSectionCenterCoordinateInRedux,
  setSelectedSectionInRedux,
  type IViewerSelectedSection,
} from "../ViewerRedux/viewerSlice";
import { selectHttpGetMcbimItem } from "../components/McBimItem/singleMcbimItemSelectors";
import { findSelectedObjectRoom, hasObjectRoomPosition } from "../ViewerRedux/objectRoomSelection";
import { selectHttpGetRoomsCurrentFloorResult } from "../Room/httpRoomSelectors";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";
import {
  addAutoPipeRoutingConnectionPointInRedux,
  selectAutoPipeRoutingPickingConnectionPointInViewer,
} from "./pipeRoutingSlice";
import { withWorldZInCoordinate } from "./devices/deviceUtils";
import { selectCachedMcbimBuildingStoriesForCurrentProject } from "./designSelector";
import { getRoomSectionCoordinate } from "../RoomSectionViews/roomSectionViewSelectors";
import {
  setDrofusOccurrenceValidationFilterModeInRedux,
  setDrofusOccurrenceValidationListTypeInRedux,
  setDrofusOccurrenceValidationResponsibilityInRedux,
} from "../DrofusOccurrenceValidation/drofusOccurrenceValidationSlice";
import { parseDrofusOccurrenceValidationColoringId } from "../DrofusOccurrenceValidation/drofusOccurrenceValidationIds";

export const selectObjectAndRoomThunk = (itemId: string | null): IAppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(setClickedMcBimItemIdInRedux(itemId));
    if (!itemId) return;

    const state = getState();
    const projectNo = state.design.projectNo;
    const floorAndJob = state.design.floorAndJob;
    const candidateItem = selectHttpGetMcbimItem(state)
      ?? selectMcBimItemsForViewer(state).find(item => String(item._id) === itemId);
    // Viewer rows may omit the floor and job required for the room query.
    const cachedItem = candidateItem && "buildingStory" in candidateItem && "job" in candidateItem
      && hasObjectRoomPosition(candidateItem) && candidateItem.buildingStory?.globalId && candidateItem.job?.jobId
      ? candidateItem : undefined;
    // Preserve the visible room while resolving the object; clearing it rebuilds room overlays.
    const previousRoom = state.design.selectedSpaceNumber;
    const isCurrentSelection = () => {
      const current = getState();
      return current.viewer.clickedMcBimItemId === itemId && current.design.projectNo === projectNo
        && current.design.floorAndJob === floorAndJob;
    };
    try {
      if (!projectNo) return;
      const item = cachedItem ?? await dispatch(openapi.endpoints.getMcbimItem.initiate(
        { projectNo, itemId }, { subscribe: false }
      )).unwrap();
      if (!isCurrentSelection() || !item || !hasObjectRoomPosition(item) || !item.buildingStory?.globalId || !item.job?.jobId) return;
      const spacesArgs = { projectNo, floorGlobalId: item.buildingStory.globalId, jobId: item.job.jobId };
      const cachedSpaces = openapi.endpoints.getMcbimSpaces.select(spacesArgs)(getState()).data;
      const spaces = cachedSpaces ?? await dispatch(openapi.endpoints.getMcbimSpaces.initiate(
        spacesArgs, { subscribe: false }
      )).unwrap();
      if (!isCurrentSelection()) return;
      const roomNumber = findSelectedObjectRoom(item, spaces);
      dispatch(setClickedObjectRoomInRedux(roomNumber ? { projectNo, itemId, roomNumber } : null));
      // Keep the object's room even if the user has since selected another room manually.
      if (getState().design.selectedSpaceNumber === previousRoom && previousRoom !== roomNumber) {
        dispatch(setSelectedSpaceNumberInRedux(roomNumber));
      }
    } catch {
      // Keep the object selected; without room geometry there is no reliable room match.
    }
  };

const getPolygonCenterFromBoundingBox = (polygon: number[][]): [number, number] => {
  if (polygon.length === 0) {
    throw new Error("Polygon must contain at least one point");
  }

  const bounds = polygon.reduce(
    (acc, [x, y]) => ({
      minX: Math.min(acc.minX, x),
      maxX: Math.max(acc.maxX, x),
      minY: Math.min(acc.minY, y),
      maxY: Math.max(acc.maxY, y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    }
  );

  return [(bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2];
};

export const thunkClickOnAPixPolygon = (
  pixPolygonId: string | undefined,
  _coordinates: [number, number]
): IAppThunk => {
  return (dispatch, getState) => {
    const state = getState();
    const mcbimSpaces = selectHttpMcbimSpacesResult(state);

    if (selectAutoPipeRoutingPickingConnectionPointInViewer(state)) {
      const clickedSpace = mcbimSpaces.find((space) => space.name === pixPolygonId);
      const polygon = clickedSpace?.polygon as number[][] | undefined;

      if (!clickedSpace || !polygon || polygon.length === 0) {
        return;
      }

      const [centerX, centerY] = getPolygonCenterFromBoundingBox(polygon);
      dispatch(
        addAutoPipeRoutingConnectionPointInRedux([
          centerX,
          centerY,
          state.design.floorAndJob?.elevation ?? 0,
        ])
      );
      return;
    }

    if (pixPolygonId == null) {
      return;
    }

    const roomSectionDirectionPrefix = "room-section-direction-";
    if (pixPolygonId.startsWith(roomSectionDirectionPrefix)) {
      const selectedSection = pixPolygonId.slice(
        roomSectionDirectionPrefix.length
      ) as IViewerSelectedSection;
      dispatch(setSelectedSectionInRedux(selectedSection));
      return;
    }

    const devices = selectHttpDevices(state);

    const device = devices.find((d) => d._id === pixPolygonId);
    if (device != null) {
      dispatch(clickOnDeviceThunk(device));
    }
    const space = mcbimSpaces.find((s) => s.name === pixPolygonId);
    if (space != null) {
      // Clicked on a space
      dispatch(thunkClickOnSpace(space.name));
      return;
    }

    const pixPolygons = selectFinalPixPolygons(state);
    const pixPolygon = pixPolygons.find((p) => p.id === pixPolygonId);

    const mcbimItmes = selectMcBimItemsForViewer(state);
    const bimItem = mcbimItmes.find((item) => item._id === pixPolygon?.entityId);

    if (bimItem) {
      dispatch(selectObjectAndRoomThunk(bimItem._id));
      dispatch(setActiveLeftSideBarTabInRedux(DesignLeftSideBarTabs.MCBIMITEM_INFO));
    }
  };
};

export const thunkHandle3dObjectClick = (guid: string): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const mcbimItems = selectMcBimItemsForViewer(state);

    const bimItem = mcbimItems.find((item) => item.globalId === guid.toString());

    if (bimItem) {
      dispatch(selectObjectAndRoomThunk(bimItem._id));
      dispatch(setActiveLeftSideBarTabInRedux(DesignLeftSideBarTabs.MCBIMITEM_INFO));
    }
  };
};

export const thunkPublishAlgoDevices = (): IAppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const devices = state.design.nonPublischedDevicesFromAlgos;
    if (state.device.savingDevices || devices.length === 0) return;
    dispatch(setSavingDevices(true));

    try {
      const rooms = selectHttpGetRoomsCurrentFloorResult(state);
      const compCodePath = selectCompCodePath(state);
      const standardDevices = new Map<string, IDevice[]>();
      const enrichedById = new Map<IDevice, IDevice>();
      for (const roomNumber of new Set(devices.map(device => device.roomNumber))) {
        const roomDevices = devices.filter(device => device.roomNumber === roomNumber);
        if (!roomDevices.some(device => !device.compCode.trim())) continue;
        const room = rooms[roomNumber];
        const standardRoom = room?.rfpMalRom;
        const projectNo = Number(roomDevices[0].projectNo);
        const key = `${projectNo}:${standardRoom}`;
        if (standardRoom && compCodePath && !standardDevices.has(key)) {
          const request = dispatch(openapi.endpoints.getMcbimItemsInSpace.initiate({ projectNo, spaceName: standardRoom }));
          try {
            const items = await request.unwrap();
            standardDevices.set(key, items.flatMap(item => {
              const device = convertMcBimItemToDevice({ mcBimItem: item, roomNumber: standardRoom, compCodePath, projectNo, ceilingHeight: 2700 });
              return device ? [device] : [];
            }));
          } finally {
            request.unsubscribe();
          }
        }
        enrichAiDevices(roomDevices, standardDevices.get(key) ?? [], room ?? {}).forEach((device, index) => enrichedById.set(roomDevices[index], device));
      }
      const formattedDevices = devices.map(device => {
        const copyOfDevice = structuredClone(enrichedById.get(device) ?? device);
        delete copyOfDevice._id;
        delete copyOfDevice.boundingBox;
        return copyOfDevice;
      });
      await dispatch(
        openapi.endpoints.postMultiplacerDeviceAddDevices.initiate({
          body: {
            devices: formattedDevices,
          },
        })
      ).unwrap();
      dispatch(openapi.util.invalidateTags(["devices"]));
      dispatch(clearDesignThunk());

      dispatch(thunkToast({ text: "Devices copied", header: "Success", isSuccess: true }));
    } catch {
      dispatch(thunkToast({ text: "Kunne ikke hente standardrom eller publisere komponenter. Forslaget er beholdt, prøv igjen.", header: "Feil", isSuccess: false }));
    } finally {
      dispatch(setSavingDevices(false));
    }
  };
};

export const thunkClickOnSpace = (spaceNr: string | undefined): IAppThunk => {
  return (dispatch, getState) => {
    if (spaceNr == null) {
      return;
    }
    const mcbimSpaces = selectHttpMcbimSpacesResult(getState());
    const rooms = selectHttpGetRoomsCurrentFloorResult(getState());
    const devices = selectHttpDevices(getState());
    const selectedSpaceNumber = getState().design.selectedSpaceNumber;
    const shiftSelectRooms = getState().design.shiftSelectRooms;
    const selectedRooms = getState().design.selectedRooms;
    const copySpaceMode = getState().design.copySpaceModeActive;
    const activeModule = getState().design.activeModule;
    const selectedRoomTab = getState().design.selectedRoomTab;
    const activeLeftSideBarTab = getState().design.activeLeftSideBarTab;
    const selectedSpace = mcbimSpaces.find((space) => space.name === spaceNr);
    const selectedSpacePolygon = selectedSpace?.polygon;

    if (copySpaceMode) {
      const spaceToCopyTo = mcbimSpaces.find((space) => space.name === spaceNr);

      if (spaceToCopyTo == null) {
        return;
      }
      const spaceToCopyFrom = mcbimSpaces.find((space) => space.name === selectedSpaceNumber);
      if (spaceToCopyFrom == null) {
        console.log("spaceToCopyFrom is null");
        return;
      }
      const roomToCopyTo = rooms?.[spaceNr];
      dispatch(
        thunkCopyDevicesFromSpaceToOtherOnSameFloor(
          spaceToCopyFrom,
          spaceToCopyTo,
          devices,
          roomToCopyTo
        )
      );
      return;
    }

    if (shiftSelectRooms) {
      if (selectedRooms.includes(spaceNr) === false) {
        dispatch(addSelectedRoomToRedux(spaceNr));
      }
      if (selectedRooms.includes(spaceNr)) {
        dispatch(removeSelectedRoomFromRedux(spaceNr));
      }
    } else {
      if (selectedRooms.length === 1) {
        dispatch(setSelectedRoomsInRedux([spaceNr]));
      }
      if (selectedRooms.length === 1 && selectedRooms[0] === spaceNr) {
        dispatch(setSelectedRoomsInRedux([]));
      }
      dispatch(setSelectedSpaceNumberInRedux(spaceNr));
      dispatch(setSectionCenterCoordinateInRedux(getRoomSectionCoordinate(selectedSpacePolygon)));
      if (activeLeftSideBarTab === DesignLeftSideBarTabs.DROFUS_CHANGES) {
        dispatch(setSelectedRoomTabInRedux(RoomSideBarTabs.ROOM_DROFUS_CHANGES));
      } else if (activeModule && selectedRoomTab == null) {
        dispatch(setSelectedRoomTabInRedux(ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE[activeModule]));
      }
    }
  };
};

export const thunkHandleRightMenu =
  (action: RIGHT_CLICK_DEVICE_MENU_ENUM, polygonId: string): IAppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const device = selectDeviceById(state, polygonId);
    if (!device) {
      return;
    }
    const room = selectActiveSingleRoomSummary(state);
    const roomIsLocked = !!room?.multiplacerCeilingDevicesLocked?.bool;

    if (roomIsLocked) {
      // NB-UI: Norska texter i UI enligt din standard
      alert("Rommet er låst");
      return;
    }

    switch (action) {
      case RIGHT_CLICK_DEVICE_MENU_ENUM.DELETE: {
        // Använd RTK Query utan hooks i thunks:
        // (Byt endpointnamn till ditt faktiska)
        await dispatch(
          openapi.endpoints.deleteMultiplacerDeviceDeleteDevice.initiate({ deviceId: polygonId })
        ).unwrap();
        return;
      }
      case RIGHT_CLICK_DEVICE_MENU_ENUM.COPY: {
        await dispatch(copyDeviceThunk(polygonId));
        return;
      }
      case RIGHT_CLICK_DEVICE_MENU_ENUM.ROTATE: {
        await dispatch(rotateDeviceThunk({ device, angle: 90 }));
        return;
      }
      default: {
        // Gör switch:en “exhaustive”
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  };

/** Minsta kontext som behövs för att fylla kraven i ZDevice */
const ZDeviceContext = z.object({
  projectNo: z.string(),
  floor: z.string(),
  roomName: z.string().optional(),
  roomNumber: z.string(),
  description: z.string().optional(),
  compCode: z.string().optional(),
});
export type IDeviceContext = z.infer<typeof ZDeviceContext>;

export const convertMlResponseToDevice = (ml: IMLPredictDevice, ctx: IDeviceContext): IDevice => {
  const { projectNo, floor, roomName, roomNumber } = ctx;
  return {
    _id: generateRandomString(10),
    compCode: ctx.compCode ?? "",
    coordinate: ml.coordinate,
    description: ctx.description ?? "",
    deviceCategory: ml.deviceCategory,
    direction: ml.direction,
    floor,
    discipline: DEVICE_DISCIPLINE_MAPPING[ml.deviceCategory] ?? DisciplineEnum.UNKNOWN,
    projectNo,
    roomName,
    roomNumber,
    bottomEdgeHeightAboveFloorMm: 2700,
  };
};

export const convertMlBatchToDevices = (mls: IMLPredictDevice[], ctx: IDeviceContext): IDevice[] =>
  mls.map((ml) => convertMlResponseToDevice(ml, ctx));

export { changeActiveModuleThunk } from "./changeActiveModuleThunk";

export const dragSelectEndThunk = (ids: string[]): IAppThunk => {
  return (dispatch, getState) => {
    dispatch(setSelectedRoomsInRedux(ids));
    dispatch(setDragSelectSpacesInRedux(false));
  };
};
export const clearDesignThunk = (): IAppThunk => {
  return (dispatch, getState) => {
    const reviewIsActive = getState().design.review.active;
    const algosInProgress = getState().design.algosInProgress;

    const aSpaceBarIsActive = reviewIsActive || algosInProgress;

    if (!aSpaceBarIsActive) {
      dispatch(setSelectedRoomsInRedux([]));
    }

    dispatch(clearNonPublischedDevicesFromAlgosInRedux());
    dispatch(setCountAlgoSpaceTotal(0));
    dispatch(setCountAlgoSpaceComplete(0));
    dispatch(setCountAlgoSpaceFailed(0));
    dispatch(resetSpaceValidationRunInRedux());
    dispatch(setDragSelectSpacesInRedux(false));
    dispatch(resetReviewModeInRedux());
    dispatch(setActiveRoomColoringIdInRedux(undefined));
    dispatch(setMeasurementToolActiveInRedux(false));
    dispatch(setCopySpaceModeActiveInRedux(false));
  };
};
export interface IRoomMcBimSpaceCombo {
  roomNumber: string;
  rfpMalRom: string;
  polygon: number[][];
  ceilingCoordinate?: number[];
  ceilingDirection?: number[];
}

export const thunkCopyDevicesFromSpaceToOtherOnSameFloor = (
  spaceToCopyFrom: PartialMcBimSpace,
  spaceToCopyTo: PartialMcBimSpace,
  devices: IDevice[],
  roomToCopyTo?: IRoomSummary
): IAppThunk => {
  return (dispatch, getState) => {
    if (spaceToCopyTo.name === spaceToCopyFrom.name) {
      return;
    }

    const nonPublischedDevicesFromAlgos = getState().design.nonPublischedDevicesFromAlgos;
    const devicesExistsInSpaceToCopyTo = devices.find(
      (device) => device.roomNumber === spaceToCopyTo.name && device.coordinate != null
    );
    if (devicesExistsInSpaceToCopyTo != null) {
      dispatch(
        thunkToast({
          text: "Det finnes allerede enheter i rommet du prøver å kopiere til",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    const existingDevicesInNonPublischedDevices = nonPublischedDevicesFromAlgos.filter(
      (device) => device.roomNumber === spaceToCopyTo.name
    );

    if (existingDevicesInNonPublischedDevices.length === 0) {
      const devicesTopCopy = devices.filter((device) => device.roomNumber === spaceToCopyFrom.name);
      const copiedDevices = copyDevicesFromASpaceToAnotherSpace({
        devicesToCopy: devicesTopCopy,
        spaceToCopyFrom,
        spaceToCopyTo,
        roomToCopyTo: roomToCopyTo,
      });
      if (copiedDevices.length === 0) {
        dispatch(
          thunkToast({
            text: `Ingen enheter kunne kopieres fordi målrommet ${spaceToCopyTo.name} skiller seg for mye fra kildrommet ${spaceToCopyFrom.name}.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        return;
      }

      const activeFloorElevationM = getState().design.floorAndJob?.elevation;
      const buildingStories = selectCachedMcbimBuildingStoriesForCurrentProject(getState());
      const buildingStory = buildingStories.find(
        (story) => story.name === spaceToCopyTo.buildingStory?.name
      );
      const heightFromFloor =
        getHeightFromFirstCeilingGrid(spaceToCopyTo.ceilingGrids ?? []) ?? 2700;
      const floorElevationM = buildingStory?.elevation ?? activeFloorElevationM;

      const enrichedCopiedDevices = copiedDevices.map((device) => ({
        ...device,
        floor: spaceToCopyTo.buildingStory?.name ?? device.floor,
        roomName: spaceToCopyTo.longName ?? device.roomName,
        bottomEdgeHeightAboveFloorMm: heightFromFloor,
        coordinate: withWorldZInCoordinate(device.coordinate, floorElevationM, heightFromFloor),
      }));

      dispatch(addNonPublischedDevicesFromAlgosInRedux(enrichedCopiedDevices));
    } else {
      dispatch(
        removeNonPublischedDevicesFromAlgosInRedux(
          existingDevicesInNonPublischedDevices.map((device) => device._id)
        )
      );
    }
  };
};

export const setActiveRoomColoringIdThunk =
  (id?: string | ROOM_COLORING_IDS | undefined) =>
  (dispatch: AppDispatch, getState: () => IRootState): void => {
    const state = getState();
    const prev = state.design.activeRoomColoringId;

    const rooms = selectHttpGetRoomsCurrentFloorResult(state);

    // Ingen endring => no-op (hindrer unødvendige rerenders)
    if (prev === id) return;

    if (id == null && prev?.startsWith("specifiedArticle")) {
      dispatch(setDrofusOccurrenceValidationListTypeInRedux(null));
      dispatch(setDrofusOccurrenceValidationResponsibilityInRedux(null));
    }

    if (id != null && id?.startsWith("specifiedArticle")) {
      dispatch(setSelectedRoomTabInRedux(RoomSideBarTabs.ROOM_SPECIFIED_ARTICLES));
      dispatch(
        setSelectedSpaceNumberInRedux(state.design.selectedSpaceNumber ?? (rooms ? Object.values(rooms)[0]?.roomNumber : undefined))
      );

      const scope = parseDrofusOccurrenceValidationColoringId(id);
      if (!scope || scope.type === "overall") {
        dispatch(setDrofusOccurrenceValidationListTypeInRedux(null));
        dispatch(setDrofusOccurrenceValidationResponsibilityInRedux(null));
      } else if (scope.type === "listType") {
        dispatch(setDrofusOccurrenceValidationFilterModeInRedux("listType"));
        dispatch(setDrofusOccurrenceValidationListTypeInRedux(scope.value));
        dispatch(setDrofusOccurrenceValidationResponsibilityInRedux(null));
      } else {
        dispatch(setDrofusOccurrenceValidationFilterModeInRedux("responsibility"));
        dispatch(setDrofusOccurrenceValidationListTypeInRedux(null));
        dispatch(setDrofusOccurrenceValidationResponsibilityInRedux(scope.value));
      }
    }

    // ---------------------------------------------------------------------------
    // Fremtidig logikk:
    // - resette andre paneler
    // - logge analytics
    // - sikre at id finnes i tilgjengelige filtre
    // - defaulting mellom standardrom/beslutning/spesifiserte artikler
    // ---------------------------------------------------------------------------

    dispatch(setActiveRoomColoringIdInRedux(id));
  };
